// -----------------------------------------------------------------------------
// wheelZoom.ts — Ctrl + mouse wheel in pop-ups (v0.7.1, v070-017 / round 25 #12, boards A5.2 / A6.4 tips):
//   "Ctrl + mouse wheel zooms the plan (not the browser)", applied throughout the app.
//   * useWheelZoom(ref, onZoom): an element with its own zoom (A6.4 gridline stage, A5.2 / A4 plan, A5.1 picture).
//     Ctrl + wheel calls onZoom(factor, clientX, clientY) and stops the browser zooming the page; a plain wheel is left
//     alone (it scrolls, or the caller pans). The element is marked data-wheel-zoom.
//   * installPopupZoom(): one listener for the whole app. Ctrl + wheel inside any other pop-up ([role=dialog]) zooms that
//     pop-up's content (CSS zoom, 60–200 %) instead of the browser; outside pop-ups the browser zooms the page as usual.
// Browsers only let a page stop Ctrl + wheel from a listener added with { passive: false } — React's onWheel can't.
// -----------------------------------------------------------------------------
import { useEffect, useRef, type RefObject } from "react";

/** One wheel notch → zoom factor (a trackpad pinch sends small deltas → small steps). Pure — unit-tested. */
export function wheelFactor(deltaY: number): number {
  const step = Math.max(-1, Math.min(1, deltaY / 100));
  return Math.pow(1.15, -step);
}

/** Next pop-up content zoom after a wheel step, kept within 60–200 %. Pure — unit-tested. */
export const popupZoom = (cur: number, factor: number) => Math.round(Math.max(0.6, Math.min(2, cur * factor)) * 100) / 100;

export function useWheelZoom(ref: RefObject<HTMLElement | null>, onZoom: (factor: number, clientX: number, clientY: number) => void, active = true) {
  const cb = useRef(onZoom);
  cb.current = onZoom;
  useEffect(() => {
    const el = ref.current; if (!el || !active) return;
    el.setAttribute("data-wheel-zoom", "1");
    const on = (e: WheelEvent) => { if (!(e.ctrlKey || e.metaKey)) return; e.preventDefault(); cb.current(wheelFactor(e.deltaY), e.clientX, e.clientY); };
    el.addEventListener("wheel", on, { passive: false });
    return () => { el.removeEventListener("wheel", on); el.removeAttribute("data-wheel-zoom"); };
  }, [ref, active]);
}

let installed = false;
export function installPopupZoom(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("wheel", (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    const t = e.target as Element | null;
    if (!t?.closest) return;
    if (t.closest("[data-wheel-zoom]")) { e.preventDefault(); return; }     // that element zooms itself
    const pop = t.closest<HTMLElement>("[role=dialog], [role=alertdialog]");
    if (!pop) return;                                                        // ordinary page: the browser zooms
    e.preventDefault();
    const z = popupZoom(Number(pop.dataset.zoom || 1), wheelFactor(e.deltaY));
    pop.dataset.zoom = String(z);
    pop.style.setProperty("zoom", z === 1 ? "" : String(z));
  }, { passive: false, capture: true });
}
